import {
  HUMAN_ASSETS,
  HUMAN_ASSET_IDS,
  HUMAN_OBJECTS,
  type HumanAssetCategory,
  type HumanAssetId,
  type HumanCollisionDefinition,
} from "@endclock/shared";
import { loadStaticGlb } from "../assets/gltf.js";
import type { Camera } from "../engine/camera.js";
import { type GpuMesh, type Renderer, type StaticRenderMaterial } from "../engine/renderer.js";
import { ReusableInstanceBatch, createStaticObjectChunks } from "../world/static-instances.js";

interface HumanManifestEntry {
  readonly id: HumanAssetId;
  readonly url: string;
  readonly category: HumanAssetCategory;
  readonly triangleCount: number;
  readonly primitiveCount: number;
  readonly collision: HumanCollisionDefinition;
  readonly boundsCenterY: number;
  readonly boundsRadius: number;
}

interface HumanManifest {
  readonly version: number;
  readonly format: string;
  readonly assets: readonly HumanManifestEntry[];
}

interface LoadedHumanPrimitive {
  readonly mesh: GpuMesh;
  readonly baseColor: readonly [number, number, number, number];
  readonly material: StaticRenderMaterial;
  readonly batch: ReusableInstanceBatch;
}

interface LoadedHumanAsset {
  readonly primitives: readonly LoadedHumanPrimitive[];
  readonly boundsCenterY: number;
  readonly boundsRadius: number;
  readonly triangleCount: number;
}

export type HumanLoadState = "loading" | "ready" | "failed";

/** Phase 6 static civilization layer using the same GLB, instance and chunk pipeline as geology and vegetation. */
export class HumanWorld {
  objects: readonly (typeof HUMAN_OBJECTS)[number][] = HUMAN_OBJECTS;
  state: HumanLoadState = "loading";
  loadError: string | undefined;
  private readonly assets = new Map<HumanAssetId, LoadedHumanAsset>();
  private chunks = createStaticObjectChunks(this.objects, (object) => {
    const definition = HUMAN_ASSETS[object.assetId];
    return {
      centerY: object.y + definition.boundsCenterY * object.scale,
      radius: definition.boundsRadius * object.scale,
    };
  });

  constructor(private readonly renderer: Renderer) {
    renderer.metrics.totalHumanObjects = this.objects.length;
    void this.load();
  }

  setObjects(objects: readonly (typeof HUMAN_OBJECTS)[number][]): void {
    this.renderer.invalidateLocalShadowCache();
    this.objects = objects;
    this.chunks = createStaticObjectChunks(objects, (object) => {
      const definition = HUMAN_ASSETS[object.assetId];
      return {
        centerY: object.y + definition.boundsCenterY * object.scale,
        radius: definition.boundsRadius * object.scale,
      };
    });
    this.renderer.metrics.totalHumanObjects = objects.length;
    if (this.state === "ready") {
      this.renderer.metrics.totalHumanTriangles = objects.reduce(
        (sum, object) => sum + (this.assets.get(object.assetId)?.triangleCount ?? 0),
        0,
      );
    }
  }

  render(camera: Camera): void {
    if (this.state !== "ready") return;
    for (const asset of this.assets.values()) {
      for (const primitive of asset.primitives) primitive.batch.reset();
    }
    for (const chunk of this.chunks) {
      if (!camera.isSphereVisible(chunk.centerX, chunk.centerY, chunk.centerZ, chunk.radius)) continue;
      for (const object of chunk.objects) {
        const asset = this.assets.get(object.assetId);
        if (!asset) continue;
        const centerY = object.y + asset.boundsCenterY * object.scale;
        if (!camera.isSphereVisible(object.x, centerY, object.z, asset.boundsRadius * object.scale)) continue;
        for (const primitive of asset.primitives) {
          if (object.terrainAlignment > 0) {
            primitive.batch.pushTerrainAligned(
              object,
              object.terrainNormal,
              object.terrainAlignment,
              primitive.baseColor,
              0.025,
              0.58,
            );
          } else {
            primitive.batch.pushUpright(object, primitive.baseColor, 0.025, 0.58);
          }
        }
        this.renderer.metrics.visibleHumanObjects += 1;
      }
    }
    for (const assetId of HUMAN_ASSET_IDS) {
      const asset = this.assets.get(assetId);
      if (!asset) continue;
      for (const primitive of asset.primitives) {
        this.renderer.drawStaticInstanced(primitive.mesh, primitive.batch.data, primitive.batch.count, primitive.material, "human");
      }
    }
  }

  private async load(): Promise<void> {
    try {
      const response = await fetch("/assets/human/manifest.json");
      if (!response.ok) throw new Error("Human asset manifest request failed");
      const manifest = (await response.json()) as HumanManifest;
      if (
        manifest.version !== 1 ||
        manifest.assets.length !== HUMAN_ASSET_IDS.length ||
        new Set(manifest.assets.map((entry) => entry.id)).size !== HUMAN_ASSET_IDS.length
      ) {
        throw new Error("Invalid human asset manifest");
      }
      const loaded = await Promise.all(
        manifest.assets.map(async (entry) => {
          const definition = HUMAN_ASSETS[entry.id];
          if (
            !definition ||
            definition.category !== entry.category ||
            !sameCollision(definition.collision, entry.collision) ||
            Math.abs(definition.boundsCenterY - entry.boundsCenterY) > 0.0001 ||
            Math.abs(definition.boundsRadius - entry.boundsRadius) > 0.0001 ||
            entry.triangleCount <= 0 ||
            entry.triangleCount > 512 ||
            entry.primitiveCount < 1 ||
            entry.primitiveCount > 3
          ) {
            throw new Error(`Human asset metadata mismatch: ${entry.id}`);
          }
          const asset = await loadStaticGlb(entry.url);
          const triangleCount = asset.primitives.reduce((sum, primitive) => sum + primitive.geometry.indices.length / 3, 0);
          if (
            asset.id !== entry.id ||
            triangleCount !== entry.triangleCount ||
            asset.primitives.length !== entry.primitiveCount ||
            !sameCollision(asset.collision, entry.collision) ||
            Math.abs(asset.boundsCenterY - entry.boundsCenterY) > 0.0001 ||
            Math.abs(asset.boundsRadius - entry.boundsRadius) > 0.0001
          ) {
            throw new Error(`Human GLB mismatch: ${entry.id}`);
          }
          return [entry.id, asset, triangleCount] as const;
        }),
      );

      for (const [id, asset, triangleCount] of loaded) {
        const primitives: LoadedHumanPrimitive[] = [];
        for (const primitive of asset.primitives) {
          if (primitive.material.alphaMode !== "OPAQUE" || primitive.material.baseColorTexture) {
            throw new Error(`Human material mismatch: ${id}`);
          }
          const mesh = this.renderer.createMesh(primitive.geometry);
          this.renderer.metrics.humanBufferBytes += mesh.bufferBytes;
          primitives.push({
            mesh,
            baseColor: primitive.material.baseColor,
            material: {
              alphaCutoff: 0, doubleSided: false,
              emissiveColor: primitive.material.emissiveColor,
              emissiveStrength: primitive.material.emissiveStrength,
              pulseStrength: primitive.material.pulseStrength,
            },
            batch: new ReusableInstanceBatch(),
          });
        }
        this.assets.set(id, {
          primitives,
          boundsCenterY: asset.boundsCenterY,
          boundsRadius: asset.boundsRadius,
          triangleCount,
        });
      }
      this.renderer.metrics.totalHumanTriangles = this.objects.reduce(
        (sum, object) => sum + this.assets.get(object.assetId)!.triangleCount,
        0,
      );
      this.state = "ready";
    } catch (error) {
      this.state = "failed";
      this.loadError = error instanceof Error ? error.message : "Unknown human asset load error";
      console.error(this.loadError);
    }
  }
}

function sameCollision(left: HumanCollisionDefinition, right: HumanCollisionDefinition): boolean {
  if (left.shape !== right.shape) return false;
  if (left.shape === "none" && right.shape === "none") return true;
  if (left.shape === "circle" && right.shape === "circle") return close(left.radius, right.radius);
  if (left.shape === "box" && right.shape === "box") {
    return close(left.halfWidth, right.halfWidth) && close(left.halfDepth, right.halfDepth);
  }
  if (left.shape === "capsule" && right.shape === "capsule") {
    return close(left.radius, right.radius) && close(left.halfLength, right.halfLength);
  }
  return false;
}

function close(left: number, right: number): boolean {
  return Math.abs(left - right) <= 0.0001;
}
